'use client';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Eye, EyeOff, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { PasswordSignUpSchema } from '../schemas/password-sign-up.schema';
import { PasswordBreachWarning } from './password-breach-warning';
import { PasswordRequirementsChecklist } from './password-requirements-checklist';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';

interface PasswordSignUpFormProps {
  defaultValues?: {
    email: string;
  };

  displayTermsCheckbox?: boolean;

  onSubmit: (params: {
    email: string;
    password: string;
    repeatPassword: string;
  }) => unknown;

  loading: boolean;
  captchaLoading: boolean;

  /**
   * When true, a quiet "Send me a magic link" secondary action renders below
   * the primary button. It reuses the same email field (no duplicate form) and
   * validates the email + Terms only — password is not required. Mirrors the
   * sign-in screen's MeteorEmailAuthForm so both screens behave identically.
   */
  magicLinkEnabled?: boolean;
  onMagicLinkSubmit?: (email: string) => unknown;
  magicLinkLoading?: boolean;
}

export function PasswordSignUpForm({
  defaultValues,
  displayTermsCheckbox,
  onSubmit,
  loading,
  captchaLoading,
  magicLinkEnabled = false,
  onMagicLinkSubmit,
  magicLinkLoading = false,
}: PasswordSignUpFormProps) {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const form = useForm({
    resolver: zodResolver(PasswordSignUpSchema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      password: '',
      repeatPassword: '',
      termsAccepted: false,
    },
  });

  // Magic link reuses the email field — validate email (+ Terms) only, since a
  // magic link needs no password. Same pattern as the sign-in screen.
  const onSendMagicLink = async () => {
    const isEmailValid = await form.trigger('email');
    if (!isEmailValid) return;

    if (displayTermsCheckbox && !form.getValues('termsAccepted')) {
      form.setError('termsAccepted', {
        type: 'manual',
        message: 'auth:errors.acceptTermsRequired',
      });
      return;
    }

    onMagicLinkSubmit?.(form.getValues('email'));
  };

  return (
    <Form {...form}>
      <form
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey={'common:emailAddress'} />
              </FormLabel>

              <FormControl>
                <Input
                  data-test={'email-input'}
                  required
                  type="email"
                  placeholder={t('auth:emailPlaceholder')}
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'password'}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey={'common:password'} />
              </FormLabel>

              <FormControl>
                <div className="relative">
                  <Input
                    required
                    data-test={'password-input'}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('auth:passwordPlaceholder')}
                    className="pr-10"
                    {...field}
                  />

                  {/* eslint-disable-next-line kit-components/use-kit-primitives -- input adornment */}
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={t(
                      showPassword ? 'auth:hidePassword' : 'auth:showPassword',
                    )}
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </FormControl>

              <FormMessage />

              <PasswordRequirementsChecklist
                password={field.value ?? ''}
                className={'pt-1'}
              />

              <PasswordBreachWarning
                password={field.value ?? ''}
                className={'pt-1'}
              />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'repeatPassword'}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey={'auth:repeatPassword'} />
              </FormLabel>

              <FormControl>
                <div className="relative">
                  <Input
                    required
                    data-test={'repeat-password-input'}
                    type={showRepeatPassword ? 'text' : 'password'}
                    placeholder={t('auth:passwordPlaceholder')}
                    className="pr-10"
                    {...field}
                  />

                  {/* eslint-disable-next-line kit-components/use-kit-primitives -- input adornment */}
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={t(
                      showRepeatPassword
                        ? 'auth:hidePassword'
                        : 'auth:showPassword',
                    )}
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowRepeatPassword((prev) => !prev)}
                  >
                    {showRepeatPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </FormControl>

              <FormMessage />

              <FormDescription className={'pb-2 text-xs'}>
                <Trans i18nKey={'auth:repeatPasswordHint'} />
              </FormDescription>
            </FormItem>
          )}
        />

        <If condition={displayTermsCheckbox}>
          <TermsAndConditionsFormField />
        </If>

        <Button
          data-test={'auth-submit-button'}
          className={'w-full'}
          type="submit"
          disabled={loading || captchaLoading}
        >
          <If condition={captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:verifyingCaptcha'} />
            </span>
          </If>

          <If condition={loading && !captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:signingUp'} />
            </span>
          </If>

          <If condition={!loading && !captchaLoading}>
            <span
              className={
                'animate-in fade-in slide-in-from-bottom-24 flex items-center'
              }
            >
              <Trans i18nKey={'auth:signUpWithEmail'} />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500'
                }
              />
            </span>
          </If>
        </Button>

        {/* Magic link — same email input, no password required. A quiet text
            link (not a full-width button) so it doesn't compete with the
            primary "Sign up" CTA. Mirrors the sign-in screen exactly. */}
        <If condition={magicLinkEnabled}>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-muted-foreground hover:text-foreground h-auto gap-1.5"
              data-test="send-magic-link-button"
              disabled={magicLinkLoading || captchaLoading}
              onClick={onSendMagicLink}
            >
              <Mail className="h-3.5 w-3.5" />
              <If condition={magicLinkLoading}>
                <Trans i18nKey={'auth:sendingEmailLink'} />
              </If>
              <If condition={!magicLinkLoading}>
                <Trans i18nKey={'auth:sendMagicLink'} />
              </If>
            </Button>
          </div>
        </If>
      </form>
    </Form>
  );
}
